import React, { useState } from 'react';
import {
  Box, Button, Container, Paper, TextField, Typography,
  Link, Alert, InputAdornment, IconButton, Stack,
  MenuItem, Select, InputLabel, FormControl, Card, CardActionArea, Grid, Checkbox, FormControlLabel,
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PersonIcon from '@mui/icons-material/Person';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import { useMutation, useQuery } from '@apollo/client';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GavelIcon from '@mui/icons-material/Gavel';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { SIGNUP_MUTATION, ME_QUERY, LOGIN_MUTATION, SIGNUP_LEGAL_DOCUMENTS_QUERY } from '../graphql/queries';
import LegalFooter from '../components/LegalFooter';
import { LegalReadDialog } from '../components/LegalAcceptanceGate';
import { useAuth } from '../context/AuthContext';
import apolloClient from '../graphql/client';
import { useCompanyDetails } from '../utils/companyDetails';

const ROLE_TILES = [
  { value: 'bride_family', label: 'Bride Family', description: 'Register as the bride\'s family to plan your wedding services', icon: PersonIcon, color: '#e91e63' },
  { value: 'groom_family', label: 'Groom Family', description: 'Register as the groom\'s family to plan your wedding services', icon: PersonIcon, color: '#1565c0' },
  { value: 'vendor', label: 'Wedding Contractor', description: 'Register as a wedding planner/contractor to manage client weddings', icon: BusinessCenterIcon, color: '#7b1fa2' },
];

export default function SignupPage() {
  const company = useCompanyDetails();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState({ email: '', password: '', roleName: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  // Terms & Privacy (and the Vendor Agreement for vendors) must be read to the end and accepted to sign up
  const [readKeys, setReadKeys] = useState(null); // the documents read and accepted (null: not yet)
  const [reading, setReading] = useState(false);
  const [declared, setDeclared] = useState(false); // 18 or older (families: for matrimony, not dating)
  const { data: legalData, loading: legalLoading } = useQuery(SIGNUP_LEGAL_DOCUMENTS_QUERY, {
    variables: { roleName: form.roleName }, skip: !form.roleName,
  });
  const legalDocs = legalData?.signupLegalDocuments || [];
  const agreeLegal = Boolean(readKeys) && declared;
  const chooseRole = (roleName) => {
    // Another account type has other documents (vendors: the Vendor Agreement too)
    if (roleName !== form.roleName) setReadKeys(null);
    setForm(f => ({ ...f, roleName }));
  };

  const [signupMutation, { loading }] = useMutation(SIGNUP_MUTATION);
  const [loginMutation] = useMutation(LOGIN_MUTATION);

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await signupMutation({ variables: { input: { email: form.email, password: form.password, roleName: form.roleName, acceptLegal: agreeLegal, legalReadInFull: readKeys || [] } } });
      const { data } = await loginMutation({ variables: { email: form.email, password: form.password } });
      const token = data.login;
      localStorage.setItem('token', token);   // set BEFORE ME_QUERY so auth header is sent
      const { data: meData } = await apolloClient.query({ query: ME_QUERY, fetchPolicy: 'network-only' });
      login(token, meData.me);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Signup failed. Please try again.');
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #4a0072 0%, #ae52d4 100%)',
      display: 'flex',
      alignItems: 'center',
    }}>
      <Container maxWidth="xs">
        <Paper elevation={6} sx={{ p: 5, borderRadius: 4 }}>
          <Stack alignItems="center" spacing={1} mb={4}>
            <FavoriteIcon sx={{ fontSize: 36, color: 'primary.main' }} />
            <Typography variant="h5" fontWeight={700}>Join {company.brandName}</Typography>
            <Typography variant="body2" color="text.secondary">
              Create your account to start planning
            </Typography>
          </Stack>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          <form onSubmit={handleSubmit}>
            <Typography variant="subtitle2" fontWeight={600} mb={1.5}>I am registering as:</Typography>
            <Grid container spacing={1.5} sx={{ mb: 3 }}>
              {ROLE_TILES.map(role => {
                const RoleIcon = role.icon;
                const isSelected = form.roleName === role.value;
                return (
                  <Grid item xs={12} key={role.value}>
                    <Card
                      elevation={isSelected ? 4 : 0}
                      sx={{
                        border: isSelected ? `2px solid ${role.color}` : '1px solid #e0e0e0',
                        borderRadius: 2,
                        bgcolor: isSelected ? `${role.color}08` : '#fff',
                        transition: '0.2s',
                      }}
                    >
                      <CardActionArea onClick={() => chooseRole(role.value)} sx={{ p: 1.5 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <RoleIcon sx={{ color: role.color, fontSize: 28 }} />
                          <Box>
                            <Typography variant="body1" fontWeight={600}>{role.label}</Typography>
                            <Typography variant="caption" color="text.secondary">{role.description}</Typography>
                          </Box>
                        </Stack>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            <TextField
              label="Email Address"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              fullWidth
              required
              sx={{ mb: 3 }}
            />
            <TextField
              label="Password"
              name="password"
              type={showPwd ? 'text' : 'password'}
              value={form.password}
              onChange={handleChange}
              fullWidth
              required
              sx={{ mb: 2 }}
              helperText="Minimum 8 characters recommended"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPwd(p => !p)} edge="end">
                      {showPwd ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, borderRadius: 2, borderColor: readKeys ? 'success.light' : 'divider' }}>
              {readKeys ? (
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <CheckCircleIcon color="success" fontSize="small" sx={{ mt: 0.25 }} />
                  <Typography variant="body2">You read and accepted the {legalDocs.map(d => d.title).join(', ')}.</Typography>
                </Stack>
              ) : (
                <Stack spacing={1}>
                  <Typography variant="body2">
                    Read and accept the Terms &amp; Conditions and the Privacy Policy
                    {form.roleName === 'vendor' ? ', and the Vendor Agreement' : ''} to create your account.
                  </Typography>
                  <Button variant="outlined" size="small" startIcon={<GavelIcon />} sx={{ alignSelf: 'flex-start' }}
                    disabled={!form.roleName || legalLoading || !legalDocs.length} onClick={() => setReading(true)}>
                    {form.roleName ? 'Read and accept' : 'Choose an account type first'}
                  </Button>
                </Stack>
              )}
            </Paper>
            <FormControlLabel
              sx={{ mb: 2, alignItems: 'flex-start' }}
              control={<Checkbox checked={declared} onChange={(e) => setDeclared(e.target.checked)} sx={{ pt: 0.25 }} />}
              label={
                <Typography variant="body2">
                  {form.roleName === 'vendor' ? 'I am 18 or older and authorised to register this business.' : 'I am 18 or older and I am using this site for matrimony (not dating).'}
                </Typography>
              }
            />
            {reading && (
              <LegalReadDialog
                documents={legalDocs}
                acceptLabel="I agree"
                onCancel={() => setReading(false)}
                onAccept={(keys) => { setReadKeys(keys); setReading(false); }}
              />
            )}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              disabled={loading || !agreeLegal}
              sx={{ fontWeight: 700, py: 1.5 }}
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          <Typography variant="body2" align="center" sx={{ mt: 3 }}>
            Already have an account?{' '}
            <Link component={RouterLink} to="/login" fontWeight={600}>
              Sign in
            </Link>
          </Typography>
          <Typography variant="body2" align="center" sx={{ mt: 1 }}>
            <Link component={RouterLink} to="/" color="text.secondary">
              Back to Home
            </Link>
          </Typography>
        </Paper>
        <LegalFooter dark sx={{ mt: 2 }} />
      </Container>
    </Box>
  );
}
